import {configureStore, combineReducers, Action} from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import projectsReducer from './slices/projectsSlice';
import transcriptionsReducer from './slices/transcriptionsSlice';
import audioReducer from './slices/audioSlice';
import conversionsReducer from './slices/conversionsSlice';
import queueReducer from './slices/queueSlice';
import wordlistsReducer from './slices/wordlistsSlice';
import subscriptionReducer from './slices/subscriptionSlice';
import usageReducer from './slices/usageSlice';
import pendingTranscriptionsReducer from './slices/pendingTranscriptionsSlice';
import signupReducer from './slices/signupSlice';
import adminReducer from './slices/adminSlice';
import {debugLog} from '../lib/debug';
import {sessionIdentityMiddleware, SESSION_USER_CHANGED} from './sessionIdentityMiddleware';

// Custom logging middleware for debugging
const loggerMiddleware = (store: any) => (next: any) => (action: any) => {
  if (import.meta.env.DEV) {
    console.group(action.type);
    debugLog('Previous State:', store.getState());
    debugLog('Action:', action);
    const result = next(action);
    debugLog('Next State:', store.getState());
    console.groupEnd();
    return result;
  }
  return next(action);
};

const appReducer = combineReducers({
  auth: authReducer,
  projects: projectsReducer,
  transcriptions: transcriptionsReducer,
  audio: audioReducer,
  conversions: conversionsReducer,
  queue: queueReducer,
  wordlists: wordlistsReducer,
  subscription: subscriptionReducer,
  usage: usageReducer,
  pendingTranscriptions: pendingTranscriptionsReducer,
  signup: signupReducer,
  admin: adminReducer,
});

/**
 * The store belongs to one user, and is emptied the moment it stops belonging to them.
 *
 * The web app never reloads on a session change, so without this every slice keeps the previous
 * account's data in memory while the next one signs in. The one that actually bit was
 * `projects.currentProject`: the new session kept fetching against the old account's project id and
 * the worker answered `403 You do not own this project`.
 *
 * Two triggers, because signing out is only one of the ways a session ends:
 *
 *   - `auth/signOut/fulfilled` — the deliberate one. (Never rejected, so `fulfilled` covers it.)
 *   - `SESSION_USER_CHANGED` — raised by `sessionIdentityMiddleware` whenever the user id in an
 *     arriving session differs from the one already in the store, which is what catches the sign-out
 *     that never ran, the session Supabase replaced on its own, and the second tab.
 *
 * Slices therefore do not need their own `auth/signOut/fulfilled` case.
 */
const rootReducer = ((state: ReturnType<typeof appReducer> | undefined, action: Action) => {
  if (action.type === 'auth/signOut/fulfilled' || action.type === SESSION_USER_CHANGED) {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
}) as typeof appReducer;

export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
                // Ignore these action types
        ignoredActions: ['auth/setSession', 'auth/setUser'],
                // Ignore these field paths in all actions
        ignoredActionPaths: ['payload.user', 'payload.session'],
                // Ignore these paths in the state
        ignoredPaths: ['auth.user', 'auth.session'],
      },
      // Identity check first: it has to see the session action, and raise its reset, before any
      // other middleware reacts to the new user.
    }).concat(sessionIdentityMiddleware, loggerMiddleware),
  devTools: import.meta.env.DEV,
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
